import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { ShoppingListItem, ShoppingList, StoreBasketResult, MixedStoreRecommendation } from '@/types/grocery';
import { stores, storeListings, getProductById } from '@/data/mockData';

interface ShoppingContextValue {
  items: ShoppingListItem[];
  addItem: (productId: string) => void;
  addScrapedItem: (item: { name: string; price: number; store: string; image?: string; size?: string; brand?: string; url?: string }) => void;
  removeItem: (productId: string) => void;
  updateQuantity: (productId: string, quantity: number) => void;
  clearList: () => void;
  savedLists: ShoppingList[];
  saveCurrentList: (name: string) => void;
  loadList: (list: ShoppingList) => void;
  deleteSavedList: (id: string) => void;
  favoriteProducts: string[];
  toggleFavoriteProduct: (id: string) => void;
  favoriteStores: string[];
  toggleFavoriteStore: (id: string) => void;
  storeBaskets: StoreBasketResult[];
  mixedRecommendation: MixedStoreRecommendation | null;
  cheapestSingleStore: StoreBasketResult | null;
}

const ShoppingContext = createContext<ShoppingContextValue | null>(null);

export function useShoppingContext() {
  const ctx = useContext(ShoppingContext);
  if (!ctx) throw new Error('useShoppingContext must be used within ShoppingProvider');
  return ctx;
}

const STORAGE_KEYS = {
  items: 'gc-items',
  savedLists: 'gc-saved-lists',
  favProducts: 'gc-fav-products',
  favStores: 'gc-fav-stores',
};

function loadJSON<T>(key: string, fallback: T): T {
  try {
    const val = localStorage.getItem(key);
    return val ? JSON.parse(val) : fallback;
  } catch {
    return fallback;
  }
}

export function ShoppingProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<ShoppingListItem[]>(() => loadJSON(STORAGE_KEYS.items, []));
  const [savedLists, setSavedLists] = useState<ShoppingList[]>(() => loadJSON(STORAGE_KEYS.savedLists, []));
  const [favoriteProducts, setFavoriteProducts] = useState<string[]>(() => loadJSON(STORAGE_KEYS.favProducts, []));
  const [favoriteStores, setFavoriteStores] = useState<string[]>(() => loadJSON(STORAGE_KEYS.favStores, []));

  useEffect(() => { localStorage.setItem(STORAGE_KEYS.items, JSON.stringify(items)); }, [items]);
  useEffect(() => { localStorage.setItem(STORAGE_KEYS.savedLists, JSON.stringify(savedLists)); }, [savedLists]);
  useEffect(() => { localStorage.setItem(STORAGE_KEYS.favProducts, JSON.stringify(favoriteProducts)); }, [favoriteProducts]);
  useEffect(() => { localStorage.setItem(STORAGE_KEYS.favStores, JSON.stringify(favoriteStores)); }, [favoriteStores]);

  const addItem = useCallback((productId: string) => {
    setItems(prev => {
      const existing = prev.find(i => i.productId === productId);
      if (existing) return prev.map(i => i.productId === productId ? { ...i, quantity: i.quantity + 1 } : i);
      return [...prev, { productId, quantity: 1 }];
    });
  }, []);

  const addScrapedItem = useCallback((item: { name: string; price: number; store: string; image?: string; size?: string; brand?: string; url?: string }) => {
    const id = `scraped-${item.name}-${item.store}`.toLowerCase().replace(/\s+/g, '-');
    setItems(prev => {
      const existing = prev.find(i => i.productId === id);
      if (existing) return prev.map(i => i.productId === id ? { ...i, quantity: i.quantity + 1 } : i);
      return [...prev, { productId: id, quantity: 1, scraped: item }];
    });
  }, []);

  const removeItem = useCallback((productId: string) => {
    setItems(prev => prev.filter(i => i.productId !== productId));
  }, []);

  const updateQuantity = useCallback((productId: string, quantity: number) => {
    if (quantity <= 0) {
      setItems(prev => prev.filter(i => i.productId !== productId));
      return;
    }
    setItems(prev => prev.map(i => i.productId === productId ? { ...i, quantity } : i));
  }, []);

  const clearList = useCallback(() => setItems([]), []);

  const saveCurrentList = useCallback((name: string) => {
    const newList: ShoppingList = {
      id: Date.now().toString(),
      name,
      items: [...items],
      createdAt: new Date().toISOString(),
    };
    setSavedLists(prev => [newList, ...prev]);
  }, [items]);

  const loadList = useCallback((list: ShoppingList) => {
    setItems([...list.items]);
  }, []);

  const deleteSavedList = useCallback((id: string) => {
    setSavedLists(prev => prev.filter(l => l.id !== id));
  }, []);

  const toggleFavoriteProduct = useCallback((id: string) => {
    setFavoriteProducts(prev => prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]);
  }, []);

  const toggleFavoriteStore = useCallback((id: string) => {
    setFavoriteStores(prev => prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]);
  }, []);

  // Compute store baskets (supports both mock & scraped items)
  const storeBaskets: StoreBasketResult[] = React.useMemo(() => {
    if (!items.length) return [];

    // Collect all unique store identifiers from both mock stores and scraped items
    const storeSet = new Map<string, string>(); // id -> name
    stores.forEach(s => storeSet.set(s.id, s.name));
    items.forEach(item => {
      if (item.scraped) {
        const sid = item.scraped.store.toLowerCase().replace(/\s+/g, '-');
        storeSet.set(sid, item.scraped.store);
      }
    });

    const baskets = Array.from(storeSet.entries()).map(([storeId]) => {
      let total = 0;
      let available = 0;

      items.forEach(item => {
        if (item.scraped) {
          // Scraped item: only available at its own store
          const sid = item.scraped.store.toLowerCase().replace(/\s+/g, '-');
          if (sid === storeId) {
            total += item.scraped.price * item.quantity;
            available++;
          }
        } else {
          const listing = storeListings.find(l => l.productId === item.productId && l.storeId === storeId && l.availability);
          if (listing) {
            total += listing.price * item.quantity;
            available++;
          }
        }
      });

      return {
        storeId,
        total: +total.toFixed(2),
        availableItems: available,
        totalItems: items.length,
        savings: 0,
      };
    }).filter(b => b.availableItems > 0);

    if (!baskets.length) return [];
    const maxTotal = Math.max(...baskets.map(b => b.total));
    baskets.forEach(b => { b.savings = +(maxTotal - b.total).toFixed(2); });
    baskets.sort((a, b) => a.total - b.total);
    return baskets;
  }, [items]);

  const cheapestSingleStore = storeBaskets.length ? storeBaskets.filter(b => b.availableItems === b.totalItems)[0] || storeBaskets[0] : null;

  // Mixed store recommendation (supports scraped items)
  const mixedRecommendation: MixedStoreRecommendation | null = React.useMemo(() => {
    if (!items.length) return null;
    const assignments: MixedStoreRecommendation['assignments'] = [];
    let total = 0;

    items.forEach(item => {
      if (item.scraped) {
        const sid = item.scraped.store.toLowerCase().replace(/\s+/g, '-');
        assignments.push({
          productId: item.productId,
          storeId: sid,
          price: item.scraped.price,
          quantity: item.quantity,
        });
        total += item.scraped.price * item.quantity;
      } else {
        const available = storeListings
          .filter(l => l.productId === item.productId && l.availability)
          .sort((a, b) => a.price - b.price);
        if (available.length) {
          const best = available[0];
          assignments.push({
            productId: item.productId,
            storeId: best.storeId,
            price: best.price,
            quantity: item.quantity,
          });
          total += best.price * item.quantity;
        }
      }
    });

    const maxStore = storeBaskets.length ? storeBaskets[storeBaskets.length - 1].total : total;
    return { assignments, total: +total.toFixed(2), savings: +(maxStore - total).toFixed(2) };
  }, [items, storeBaskets]);

  return (
    <ShoppingContext.Provider value={{
      items, addItem, addScrapedItem, removeItem, updateQuantity, clearList,
      savedLists, saveCurrentList, loadList, deleteSavedList,
      favoriteProducts, toggleFavoriteProduct,
      favoriteStores, toggleFavoriteStore,
      storeBaskets, mixedRecommendation, cheapestSingleStore,
    }}>
      {children}
    </ShoppingContext.Provider>
  );
}
